// main.ts — boot. Error capture -> save load -> content registration -> sprite
// bake -> audio init -> screen router -> test API.

// Zone/boss content self-registers on import. Stubs are inert; the descent
// chain simply ends at the deepest registered zone, so the game always runs.
import './content/zones/zone1'
import './content/bosses/boss1'
import './content/zones/zone2'
import './content/bosses/boss2'
import './content/zones/zone3'
import './content/bosses/boss3'
import './content/zones/zone4'
import './content/bosses/boss4'
import './content/zones/zone5'
import './content/bosses/boss5'
import './content/zones/zone6'
import './content/bosses/boss6'

import type { SaveData, TestApi } from './types'
import { game } from './game/game'
import { meta } from './game/meta'
import { audio } from './engine/audio'
import { virtual } from './engine/input'
import { hideSummary, showSummary } from './ui/summary'
import { hideHub, showHub } from './ui/hub'

// --------------------------------------------------------------- error capture
// Snapshot.errors is what the Playwright loop asserts on, so every path that can
// produce an error has to land in the same bucket.

const nativeError = console.error.bind(console)
console.error = (...args: unknown[]): void => {
  try {
    game.recordError(args.map((a) => (a instanceof Error ? a.message : String(a))).join(' '))
  } catch {
    /* ignore */
  }
  nativeError(...args)
}

window.addEventListener('error', (e) => {
  game.recordError(e.error ?? e.message)
})
window.addEventListener('unhandledrejection', (e) => {
  game.recordError(e.reason)
})

// --------------------------------------------------------------------- boot

const canvas = document.getElementById('game') as HTMLCanvasElement | null
if (!canvas) {
  throw new Error('#game canvas missing from index.html')
}

audio.init()
game.boot(canvas)

// The loop of record (DESIGN §8): run ends -> itemised summary -> the Threshold
// -> the gate -> run. R is kept as the impatient path straight back down.
game.onSummaryShown((s) =>
  showSummary(
    s,
    () => game.startRun(),
    () => game.toHub(),
  ),
)
game.onScreen((screen) => {
  if (screen !== 'summary') hideSummary()
  if (screen === 'hub') showHub(() => game.startRun())
  else hideHub()
})

// ------------------------------------------------------------------ test API

const api: TestApi = {
  version: '0.5.0-phase5',
  state: () => game.snapshot(),
  input: virtual,
  setTimescale(n: number): void {
    if (!Number.isFinite(n) || n <= 0) return
    game.loop.timescale = Math.min(64, n)
  },
  seedNext(seed: number): void {
    game.seedNext(seed)
  },
  grantMeta(
    patch: Partial<Pick<SaveData, 'embers' | 'crests' | 'zonesReached'>> & {
      shrineAll?: boolean
      stockAll?: boolean
    },
  ): void {
    meta.grant(patch)
    // The hub is a rendered snapshot of the save: re-render it in place so an
    // injected grant is visible without a screen change.
    if (game.screen === 'hub') showHub(() => game.startRun())
  },
  grantGold(n: number): void {
    game.run?.addGold(n)
  },
  startRun(): void {
    game.startRun()
  },
  toHub(): void {
    game.toHub()
  },
  reset(): void {
    meta.reset()
    game.toTitle()
  },
}

window.__gl = api
